// src/components/RankingPopup.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Sonometer } from "sonometer/types/sonometer.model";
import { SonometerService } from "../../services/sonometer.service";
import { Loader } from "@mantine/core";
import { assetBaseUrl } from "./TextPopup";
import { ChampionshipService } from "services/championship.service";

export interface RankingRow {
  index: number;
  team: string;
  value: number;
  isHighlighted?: boolean;
}

interface RankingPopupProps {
  currentRank?: number;
  sonometer?: Sonometer;
}

interface ChampionshipSonometerRankingResponse {
  championshipId: string;
  totalSonometers: number;
  rank1Sonometers: Array<Sonometer & { rank: number }>;
  rank2Sonometers: Array<Sonometer & { rank: number }>;
  rank3Sonometers: Array<Sonometer & { rank: number }>;
  queriedSonometer: Array<Sonometer & { rank: number }>;
}

const sonometerService = new SonometerService();

const formatRank = (rank: number): string => {
  if (rank === 1) {
    return `${rank}er`;
  } else {
    return `${rank}eme`;
  }
};

const RankingPopup: React.FC<RankingPopupProps> = ({
  currentRank = 13,
  sonometer,
}) => {
  const [rankingData, setRankingData] = useState<RankingRow[]>([]);
  const [actualCurrentRank, setActualCurrentRank] = useState<number | null>(
    null
  );

  useEffect(() => {
    const fetchRanking = async () => {
      if (!sonometer?.championshipId || !sonometer?.id) {
        return;
      }

      try {
        const response: ChampionshipSonometerRankingResponse =
          await sonometerService.calculateChampionshipSonometerRanking({
            championshipId: sonometer.championshipId,
            sonometerId: sonometer.id,
          });

        const {
          totalSonometers,
          rank1Sonometers = [],
          rank2Sonometers = [],
          rank3Sonometers = [],
          queriedSonometer = [],
        } = response;

        // Validate that we have the queried sonometer data
        if (!queriedSonometer || queriedSonometer.length === 0) {
          console.warn("No queried sonometer data found");
          return;
        }

        const queriedSono = queriedSonometer[0];
        if (
          !queriedSono?.rank ||
          !queriedSono?.opponentName ||
          queriedSono?.score == null
        ) {
          console.warn("Invalid queried sonometer data");
          return;
        }

        setActualCurrentRank(queriedSono.rank);

        let mappedData: RankingRow[] = [];

        // Helper function to safely create ranking row
        const createRankingRow = (
          sono: any,
          highlighted: boolean = false
        ): RankingRow | null => {
          if (
            !sono ||
            sono.rank == null ||
            !sono.opponentName ||
            sono.score == null
          ) {
            return null;
          }
          return {
            index: sono.rank,
            team: sono.opponentName,
            value: sono.score,
            isHighlighted: highlighted,
          };
        };

        const queriedSonoRank = queriedSono.rank;

        if (totalSonometers === 2) {
          // Case for 2 sonometers only
          if (queriedSonoRank === 1) {
            const rank1First = rank1Sonometers[0];
            const rank2First = rank2Sonometers[0];
            const row1 = createRankingRow(rank1First, true);
            const row2 = createRankingRow(rank2First, false);
            mappedData = [row1, row2].filter(Boolean) as RankingRow[];
          } else if (queriedSonoRank === 2) {
            const rank1First = rank1Sonometers[0];
            const rank2First = rank2Sonometers[0];
            const row1 = createRankingRow(rank1First, false);
            const row2 = createRankingRow(rank2First, true);
            mappedData = [row1, row2].filter(Boolean) as RankingRow[];
          }
        } else {
          // Case for more than 2 sonometers
          if (queriedSonoRank === 1) {
            const rank1First = rank1Sonometers[0];
            const rank2First = rank2Sonometers[0];
            const rank3First = rank3Sonometers[0];
            const row1 = createRankingRow(rank1First, true);
            const row2 = createRankingRow(rank2First, false);
            const row3 = createRankingRow(rank3First, false);
            mappedData = [row1, row2, row3].filter(Boolean) as RankingRow[];
          } else if (queriedSonoRank === 2) {
            const rank1First = rank1Sonometers[0];
            const rank2First = rank2Sonometers[0];
            const rank3First = rank3Sonometers[0];
            const row1 = createRankingRow(rank1First, false);
            const row2 = createRankingRow(rank2First, true);
            const row3 = createRankingRow(rank3First, false);
            mappedData = [row1, row2, row3].filter(Boolean) as RankingRow[];
          } else {
            // Default case: Show rank1 leader, rank2 leader, and current sonometer
            const rank1First = rank1Sonometers[0];
            const rank2First = rank2Sonometers[0];

            const row1 = createRankingRow(rank1First, false);
            const row2 = createRankingRow(rank2First, false);
            const row3 = createRankingRow(queriedSono, true);

            mappedData = [row1, row2, row3].filter(Boolean) as RankingRow[];
          }
        }

        setRankingData(mappedData);
      } catch (error) {
        console.error("Error fetching sonometer ranking:", error);
        // Set empty data on error
        setRankingData([]);
      }
    };

    fetchRanking();
  }, [sonometer]);
  const championshipService = useMemo(() => new ChampionshipService(), []);
  useEffect(() => {
    const fetchChampionship = async () => {
      if (sonometer?.championshipId) {
        try {
          const championshipData =
            await championshipService.fetchChampionshipData(
              sonometer.championshipId
            );
          if (championshipData && championshipData.maxScore) {
            localStorage.setItem(
              `championshipMaxScore-${sonometer.championshipId}`,
              championshipData.maxScore?.toString()
            );
          }
        } catch (error) {
          console.error("Error fetching championship data:", error);
        }
      }
    };

    fetchChampionship();
  }, [sonometer, championshipService]);

  return (
    <div className="w-[1540px] h-[220px] overflow-hidden font-virage">
      <div className="flex h-full">
        {/* Left Sponsor */}
        <div className="w-[260px] h-[220px] flex items-center justify-center">
          <img
            src={
              sonometer?.publicScreenSettings.sponsorLogo ||
              assetBaseUrl + "/images/sponsor_left.png"
            }
            alt="Sponsor"
            className="max-w-full max-h-full"
          />
        </div>

        {/* Margin */}
        <div className="w-[200px]"></div>

        {/* Ranking Section */}
        <div className="w-[620px] flex flex-col justify-start items-center">
          <div className="h-[23px]"></div>

          {rankingData.length > 0 ? (
            rankingData.map((row) => (
              <div
                key={row.index}
                className={`w-[620px] h-[58px] mx-[5px] flex flex-row justify-center items-center rounded-[10px] ${
                  row.isHighlighted ? "border-[3px] border-psg-red" : ""
                }`}
              >
                <div className="w-[80px] h-auto text-right text-[36px]">
                  {row.index} -
                </div>

                <div className="w-[380px] h-auto text-left text-[36px] pl-[7px] overflow-hidden text-ellipsis whitespace-nowrap">
                  PSG vs {row.team}
                </div>

                <div className="flex justify-end items-center w-[140px] h-auto text-right text-[30px]">
                  {row.value.toFixed(1)} dB
                </div>
              </div>
            ))
          ) : (
            // Fallback when no ranking data is available
            <div className="w-[590px] h-[58px] mx-[5px] flex flex-row justify-center items-center text-[36px]">
              <Loader size="lg" className="mr-4" />
              Loading ranking...
            </div>
          )}

          <div className="h-[23px]"></div>
        </div>

        {/* Right Section */}
        <div
          className="h-[220px] w-[460px] flex items-center justify-center text-[90px]"
          style={{
            color: sonometer?.publicScreenSettings?.textColor || "#FFFFFF",
          }}
        >
          {actualCurrentRank ? formatRank(actualCurrentRank) + " !" : ""}
        </div>
      </div>
    </div>
  );
};

export default RankingPopup;
